import { Suspense, lazy, useEffect, useState, type ComponentType } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { useUser } from "@client/hooks/useAuth";
import { AppLayout } from "@client/layouts/AppLayout";
import { ErrorBoundary } from "@client/components/ErrorBoundary";
import {
  BlockSkeleton, CardSkeleton, ChatSkeleton, KanbanSkeleton, ListSkeleton, TableSkeleton,
} from "@client/components/LoadingSkeleton";
import { Login } from "@client/pages/Login";
import type { PipelineKey } from "@shared/types/index";

/**
 * Every screen is its own chunk, so the shell stays small and a screen that fails to load does not
 * take the app with it. Modelled on the reference system's `tela()` wrapper, which lazy-loads all 48
 * of its screens for the same reason.
 */
const screen = <P extends object = Record<never, never>>(
  load: () => Promise<Record<string, unknown>>, name: string,
) => lazy(async () => ({ default: (await load())[name] as ComponentType<P> }));

const Hoje = screen(() => import("@client/pages/Hoje"), "Hoje");
const Dashboard = screen(() => import("@client/pages/dashboard"), "Dashboard");
const Conversas = screen(() => import("@client/pages/Conversas"), "Conversas");
const Quadro = screen<{ board: PipelineKey }>(() => import("@client/pages/boards"), "Quadro");
const Pacientes = screen(() => import("@client/pages/pacientes"), "Pacientes");
const Agenda = screen(() => import("@client/pages/agenda"), "Agenda");
const Configuracoes = screen(() => import("@client/pages/configuracoes"), "Configuracoes");
const Agendar = screen<{ handle: string; type?: string | undefined }>(
  () => import("@client/pages/agendar"), "Agendar");

const Convite = screen<{ token: string }>(() => import("@client/pages/Convite"), "Convite");
/** `/`: the public landing page. Its own chunk, so the app never downloads it. */
const Landing = screen(() => import("@client/pages/landing"), "Landing");
/**
 * `/admin`: the platform back office (ADR-057). Its own chunk and its own shell — a nutritionist
 * never downloads it, and `AppLayout` would be wrong around it: that shell opens the inbox stream
 * and paints the signed-in tenant's colours, neither of which means anything above the clinics.
 */
const Admin = screen(() => import("@client/pages/admin"), "Admin");

/** `/convite/<tenantId>.<token>`: set a password from an invite (ADR-045). */
const INVITE = /^\/convite\/([0-9a-f-]{36}\.[A-Za-z0-9_-]+)\/?$/;

/** `/<handle>/agendar[/<type>]`: the public booking page (ADR-043). */
const PUBLIC_BOOKING = /^\/([a-z0-9][a-z0-9-]{1,28}[a-z0-9])\/agendar(?:\/([a-z0-9-]+))?\/?$/;

/**
 * The skeleton for the route being loaded, and only once the wait is worth showing.
 *
 * On a warm cache a lazy chunk resolves in a few milliseconds, so an unconditional fallback paints
 * a skeleton for one frame and tears it away again — a flash that reads as a glitch rather than as
 * loading. 120ms is the usual threshold: below it people perceive the navigation as instant, so
 * showing anything is worse than showing nothing.
 */
function RouteFallback({ location }: { location: string }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShow(true), 120);
    return () => window.clearTimeout(t);
  }, []);
  if (!show) return null;

  const body = location.startsWith("/app/conversas") ? <ChatSkeleton />
    : location.startsWith("/app/comercial") || location.startsWith("/app/acompanhamento")
      ? <KanbanSkeleton />
    : location.startsWith("/app/hoje") || location.startsWith("/app/dashboard")
      ? <BlockSkeleton />
    : location.startsWith("/app/pacientes") || location.startsWith("/app/contatos")
      ? <TableSkeleton />
    : <ListSkeleton />;
  return <div className="animate-fade-in p-4 md:p-6">{body}</div>;
}

export function App() {
  const [location] = useLocation();
  // Before anything asks who is logged in: a visitor has no session, and the session check would
  // send them to /login.
  if (location === "/") {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div className="min-h-screen bg-[#09141d]" />}><Landing /></Suspense>
      </ErrorBoundary>
    );
  }
  const invite = INVITE.exec(location);
  if (invite) {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}><Convite token={invite[1]!} /></Suspense>
      </ErrorBoundary>
    );
  }
  const booking = PUBLIC_BOOKING.exec(location);
  if (booking) {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <Agendar handle={booking[1]!} type={booking[2]} />
        </Suspense>
      </ErrorBoundary>
    );
  }
  return <SignedIn location={location} />;
}

function SignedIn({ location }: { location: string }) {
  const { data: user, isLoading, isError } = useUser();

  if (location === "/login") {
    return user ? <Redirect to="/app/hoje" /> : <Login />;
  }
  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="w-full max-w-sm"><CardSkeleton /></div>
      </div>
    );
  }
  if (isError || !user) return <Redirect to="/login" />;
  if (location === "/app") return <Redirect to="/app/hoje" />;
  // Signed in, but outside the clinic shell: the admin area brings its own.
  if (location === "/admin") return <Redirect to="/admin/visao-geral" />;
  if (location.startsWith("/admin/")) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div className="min-h-dvh bg-background" />}><Admin /></Suspense>
      </ErrorBoundary>
    );
  }
  const screenKey = location.split("/").slice(0, 3).join("/");

  return (
    <AppLayout>
      <ErrorBoundary>
        {/*
          A fallback shaped like the route you are going to, and keyed to it.

          One generic `ListSkeleton` stood in for all nine screens, so arriving at the inbox or a
          board showed five list rows and then hard-cut to a completely different layout. That is
          the failure docs/06-design-system.md names exactly: a skeleton that does not match "does
          not remove the flicker, it relocates it". `ChatSkeleton`, `KanbanSkeleton` and the rest
          already existed and were simply never wired up here.

          `key` matters as much as the shape: without it React keeps one Suspense boundary across
          navigations and the fallback does not re-mount, so the delay guard below never re-arms.
          It is the *screen* ("/app/conversas"), not the path: keyed to the path, opening another
          chat threw the whole inbox away and built it again, so the list jumped back to the top
          and lost its search and loaded pages (22/10). A screen that shows one thing at a time
          keys that thing itself (Thread, Profile, a settings section).
        */}
        <Suspense key={screenKey} fallback={<RouteFallback location={location} />}>
          <Switch>
            <Route path="/app/hoje" component={Hoje} />
            <Route path="/app/dashboard" component={Dashboard} />
            <Route path="/app/conversas/:id?" component={Conversas} />
            {/*
              A fila is a tab of Conversas now, not a page. The redirect stays because the link is
              in people's history and in the two screens that point at it — and because the old
              page and the tab were never the same list: one required `waiting_since`, the other
              filtered on `status = 'open'`, so each showed rows the other did not.
            */}
            <Route path="/app/fila"><Redirect to="/app/conversas?tab=fila" replace /></Route>
            <Route path="/app/comercial">{() => <Quadro board="commercial" />}</Route>
            <Route path="/app/acompanhamento">{() => <Quadro board="care" />}</Route>
            {/* A contact who is not a patient has the same page, by contact id (design pass, page 6). */}
            <Route path="/app/pacientes/contato/:contactId" component={Pacientes} />
            <Route path="/app/pacientes/:id?" component={Pacientes} />
            {/* Contatos is a view of Pacientes now; old links land on it. */}
            <Route path="/app/contatos"><Redirect to="/app/pacientes?ver=contatos" replace /></Route>
            <Route path="/app/agenda" component={Agenda} />
            <Route path="/app/configuracoes/:secao?" component={Configuracoes} />
            <Route>
              <div className="p-10 text-center text-muted-foreground">
                Essa página não existe.
              </div>
            </Route>
          </Switch>
        </Suspense>
      </ErrorBoundary>
    </AppLayout>
  );
}
